import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, MailPlus, DollarSign, Zap, CheckCircle2, Lock, Info } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Switch } from '../../../ui/switch';
import trimboxLogo from '../../../../assets/trimbox-logo.svg';
import { APP_STORE_LINK, PLAY_STORE_LINK } from '../../../../constants';
import { openAppStoreLink } from '../../../../utils/appStoreUtils';
import ZendeskService from '../../../../services/zendeskService';

// SINGLE-FILE, PRODUCTION-READY LANDING PAGE
// Tailwind + shadcn/ui + Framer Motion. Minimal JS, accessible, and responsive.
// Replace placeholder links and hook up forms to your backend as needed.

export default function TrimboxGatedLanding() {
  return (
    <div
      className='min-h-screen bg-gradient-to-b from-white to-slate-50 text-slate-900'
      style={{ width: '100%' }}
    >
      <Header />
      <Hero />
      <LogosStrip />
      <HowItWorks />
      <EarningsCalculator />
      <SafetyTrust />
      <Callouts />
      <FAQ />
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className='sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-slate-200/60'>
      <div className='mx-auto max-w-6xl px-4 py-3 flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <img src={trimboxLogo} alt='Trimbox' className='h-8 w-8' />
          <span className='font-semibold tracking-tight'>Trimbox</span>
        </div>
        <nav className='hidden md:flex items-center gap-6 text-sm'>
          <a href='#how' className='hover:text-blue-600'>
            How it works
          </a>
          <a href='#calc' className='hover:text-blue-600'>
            Earnings
          </a>
          <a href='#safety' className='hover:text-blue-600'>
            Safety
          </a>
          <a href='#faq' className='hover:text-blue-600'>
            FAQ
          </a>
        </nav>
        <div className='flex items-center gap-2'>
          {/* <Button onClick={next} className='bg-blue-600 hover:bg-blue-700'>
            Get started
          </Button> */}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className='relative overflow-hidden'>
      <div className='mx-auto max-w-6xl px-4 py-20 grid md:grid-cols-2 gap-10 items-center'>
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className='text-4xl md:text-5xl font-semibold leading-tight tracking-tight'
          >
            Get paid to receive emails.
          </motion.h1>
          <p className='mt-5 text-slate-600 text-lg'>
            Stop letting strangers waste your time for free. With Trimbox, unknown senders must pay
            you to reach your inbox. Every dollar goes straight to your pocket—no fees, no
            commissions.
          </p>
          <ul className='mt-6 space-y-3 text-slate-700'>
            <li className='flex items-center gap-2'>
              <CheckCircle2 className='h-5 w-5 text-blue-600' /> Earn $1+ for every unwanted email
            </li>
            <li className='flex items-center gap-2'>
              <CheckCircle2 className='h-5 w-5 text-blue-600' /> Keep your inbox clean while making
              money
            </li>
            <li className='flex items-center gap-2'>
              <CheckCircle2 className='h-5 w-5 text-blue-600' /> Set your own price and charge what
              your time is worth
            </li>
          </ul>

          <div className='mt-2 sm:mt-8 flex flex-col sm:flex-row gap-0 sm:gap-4'>
            <StoreButton storeType='app' />
            <StoreButton storeType='play' />
          </div>
          <p className='mt-2 text-xs text-slate-500'>
            By getting started, you agree to our{' '}
            <a className='underline' href='https://www.trimbox.io/terms'>
              Terms
            </a>{' '}
            and{' '}
            <a className='underline' href='https://www.trimbox.io/privacy'>
              Privacy Policy
            </a>
            .
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className='relative'
        >
          <PreviewCard />
        </motion.div>
      </div>
      <div className='pointer-events-none absolute -top-20 -right-24 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl' />
    </section>
  );
}

function PreviewCard() {
  return (
    <Card className='rounded-2xl shadow-xl border-slate-200'>
      <CardHeader>
        <CardTitle className='flex items-center gap-2 text-base'>
          <span className='inline-flex h-8 w-8 items-center justify-center rounded-xl bg-blue-100'>
            <MailPlus className='h-4 w-4 text-blue-700' />
          </span>
          Example: New sender ➜ pay to reach you
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-5'>
        <div className='rounded-xl border p-4'>
          <div className='flex items-center justify-between'>
            <div>
              <div className='font-medium'>sales@widgetco.com</div>
              <div className='text-sm text-slate-500'>Subject: Quick intro from WidgetCo</div>
            </div>
            <span className='text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-1'>
              Unrecognized
            </span>
          </div>
          <div className='mt-3 text-sm text-slate-600'>
            This email has been moved to your <strong>Requests</strong> folder. You can approve the
            sender or collect a $1 fee.
          </div>
          <div className='mt-4 flex gap-2'>
            <Button variant='outline' className='gap-1'>
              <CheckCircle2 className='h-4 w-4' />
              Approve
            </Button>
            <Button variant='secondary' className='gap-1'>
              <DollarSign className='h-4 w-4' />
              Collect $1
            </Button>
          </div>
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div className='rounded-xl border p-4'>
            <div className='text-sm text-slate-500 mb-1'>Known contacts</div>
            <div className='font-semibold'>Free access</div>
          </div>
          <div className='rounded-xl border p-4'>
            <div className='text-sm text-slate-500 mb-1'>Unknown senders</div>
            <div className='font-semibold'>Pay to reach you</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LogosStrip() {
  return (
    <section className='py-10'>
      <div className='mx-auto max-w-6xl px-4'>
        <div className='text-center text-sm text-slate-500'>Works with major email providers*</div>
        <div className='mt-4 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-6 items-center opacity-80'>
          {['Gmail', 'Outlook', 'iCloud', 'Yahoo', 'Fastmail', 'Proton'].map((name) => (
            <div key={name} className='text-center border rounded-xl py-3 text-slate-500 text-sm'>
              {name}
            </div>
          ))}
        </div>
        <p className='text-[11px] text-slate-400 mt-3 text-center'>
          *Compatibility may vary by account type and settings.
        </p>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: <ShieldCheck className='h-5 w-5' />,
      title: 'Connect & start earning',
      desc: 'Link your email and immediately start monetizing unwanted messages. Unknown senders get routed to your Requests folder—your inbox stays clean while you earn.',
    },
    {
      icon: <DollarSign className='h-5 w-5' />,
      title: 'Set your price',
      desc: 'Charge $1 by default or set higher prices for different types of outreach. Your time is valuable—make them pay for it.',
    },
    {
      icon: <Zap className='h-5 w-5' />,
      title: 'Get paid instantly',
      desc: 'Approve in one tap or collect payment when strangers need your attention. Money hits your account immediately.',
    },
  ];

  return (
    <section id='how' className='py-14 bg-white'>
      <div className='mx-auto max-w-6xl px-4'>
        <h2 className='text-2xl md:text-3xl font-semibold tracking-tight'>How to start earning</h2>
        <div className='mt-8 grid md:grid-cols-3 gap-5'>
          {steps.map((s, i) => (
            <Card key={i} className='rounded-2xl'>
              <CardHeader className='flex flex-row items-center gap-3'>
                <div className='h-9 w-9 rounded-full bg-blue-100 grid place-items-center text-blue-800'>
                  {s.icon}
                </div>
                <CardTitle className='text-lg'>{s.title}</CardTitle>
              </CardHeader>
              <CardContent className='text-slate-600'>{s.desc}</CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function EarningsCalculator() {
  const [unknownPerWeek, setUnknownPerWeek] = useState(12);
  const [price, setPrice] = useState(1);
  const [approvalRate, setApprovalRate] = useState(true); // toggle for 100% delivery vs tolled
  const weekly = useMemo(
    () => unknownPerWeek * price * (approvalRate ? 0.5 : 1),
    [unknownPerWeek, price, approvalRate]
  );
  const monthly = Math.round(weekly * 4.33 * 100) / 100;
  const yearly = Math.round(monthly * 12 * 100) / 100;

  return (
    <section id='calc' className='py-14'>
      <div className='mx-auto max-w-6xl px-4'>
        <div className='grid lg:grid-cols-2 gap-10 items-start'>
          <div>
            <h2 className='text-2xl md:text-3xl font-semibold tracking-tight'>
              Calculate your potential earnings
            </h2>
            <p className='mt-2 text-slate-600'>
              See how much you could earn by monetizing your inbox. Adjust your price and volume to
              maximize your income.
            </p>

            <div className='mt-6 grid sm:grid-cols-2 gap-4'>
              <LabeledInput
                label='Unknown senders per week'
                value={unknownPerWeek}
                onChange={setUnknownPerWeek}
                min={0}
                max={200}
                step={1}
              />
              <LabeledInput
                label='Your price per email ($)'
                value={price}
                onChange={setPrice}
                min={0}
                max={50}
                step={0.5}
              />
              <div className='col-span-full flex items-center justify-between rounded-2xl border p-4'>
                <div>
                  <div className='font-medium'>Realistic vs optimistic earnings</div>
                  <p className='text-sm text-slate-600'>
                    Toggle to compare. With approvals on, ~half may bypass payment after approval.
                  </p>
                </div>
                <Switch
                  checked={approvalRate}
                  onCheckedChange={setApprovalRate}
                  aria-label='Assume 50% pay'
                />
              </div>
            </div>
          </div>

          <Card className='rounded-2xl shadow-md'>
            <CardHeader>
              <CardTitle>Your potential income</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 text-center'>
                <div className='rounded-xl border p-4 sm:p-5'>
                  <div className='text-xs text-slate-500'>Monthly</div>
                  <div className='text-2xl sm:text-3xl font-semibold text-green-600'>
                    ${monthly.toLocaleString()}
                  </div>
                </div>
                <div className='rounded-xl border p-4 sm:p-5'>
                  <div className='text-xs text-slate-500'>Yearly</div>
                  <div className='text-2xl sm:text-3xl font-semibold text-green-600'>
                    ${yearly.toLocaleString()}
                  </div>
                </div>
                <div className='rounded-xl border p-4 sm:p-5'>
                  <div className='text-xs text-slate-500'>Payment rate</div>
                  <div className='text-2xl sm:text-3xl font-semibold'>
                    {approvalRate ? '50%' : '100%'}
                  </div>
                </div>
              </div>
              <p className='mt-3 text-[11px] text-slate-500'>
                For illustration only. Actual earnings depend on your email volume and sender
                behavior.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step: number;
}) {
  return (
    <label className='grid gap-2'>
      <span className='text-sm font-medium'>{label}</span>
      <div className='flex items-center gap-3'>
        <input
          type='range'
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className='w-full accent-blue-600'
        />
        <Input
          type='number'
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          className='w-28'
        />
      </div>
    </label>
  );
}

function SafetyTrust() {
  const items = [
    {
      icon: <Lock className='h-5 w-5' />,
      title: 'Secure payments',
      desc: 'All payments are processed securely through trusted payment providers. You get paid instantly with clear transaction records.',
    },
    {
      icon: <ShieldCheck className='h-5 w-5' />,
      title: 'You control everything',
      desc: 'Set your own prices, approve who you want, and withdraw your earnings anytime. No hidden fees or surprises.',
    },
    {
      icon: <Info className='h-5 w-5' />,
      title: 'Transparent for everyone',
      desc: 'Senders see exactly what they need to pay. You see exactly what you earn. No confusion, no disputes.',
    },
  ];

  return (
    <section id='safety' className='py-14 bg-white'>
      <div className='mx-auto max-w-6xl px-4'>
        <div className='grid md:grid-cols-2 gap-10'>
          <div>
            <h2 className='text-2xl md:text-3xl font-semibold tracking-tight'>Safe & profitable</h2>
            <p className='mt-2 text-slate-600'>
              Built to maximize your earnings while keeping everything secure and compliant. We
              handle the complexity so you can focus on getting paid.
            </p>
            <ul className='mt-5 grid gap-3'>
              {items.map((it, i) => (
                <li key={i} className='flex items-start gap-3'>
                  <div className='h-9 w-9 rounded-full bg-blue-100 grid place-items-center text-blue-800'>
                    {it.icon}
                  </div>
                  <div>
                    <div className='font-medium'>{it.title}</div>
                    <div className='text-slate-600 text-sm'>{it.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <Card className='rounded-2xl'>
            <CardHeader>
              <CardTitle>Payment & security</CardTitle>
            </CardHeader>
            <CardContent className='text-sm text-slate-700 space-y-3'>
              <p>
                • Instant payments to your connected account. No waiting periods or minimum
                thresholds.
              </p>
              <p>
                • Secure OAuth integration with your email provider. We never see your password or
                email content.
              </p>
              <p>• Clear receipts for every payment. Easy dispute resolution if needed.</p>
              <p className='text-[11px] text-slate-500'>
                Payment processing and security measures may evolve as we add new features and
                providers.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Callouts() {
  const blocks = [
    {
      title: 'One-tap approvals',
      text: 'Recognize a sender? Approve once and future emails arrive free—no lost revenue from people you actually want to hear from.',
    },
    {
      title: 'Smart organization',
      text: 'Auto-organized folders keep your earning opportunities separate from your important messages.',
    },
    {
      title: 'Flexible pricing',
      text: 'Use $1 by default or set custom prices for different categories. Charge what your time is worth.',
    },
  ];
  return (
    <section className='py-14'>
      <div className='mx-auto max-w-6xl px-4'>
        <div className='grid md:grid-cols-3 gap-5'>
          {blocks.map((b, i) => (
            <Card key={i} className='rounded-2xl'>
              <CardHeader>
                <CardTitle className='text-lg'>{b.title}</CardTitle>
              </CardHeader>
              <CardContent className='text-slate-600'>{b.text}</CardContent>
            </Card>
          ))}
        </div>
        <div className='mt-6 sm:mt-10 rounded-2xl border p-6 flex flex-col sm:flex-row items-center justify-between gap-4'>
          <div>
            <div className='text-xl font-semibold'>Start earning from your inbox today</div>
            <div className='text-slate-600'>
              Join early access and be among the first to monetize your email attention.
            </div>
          </div>
          <div className='flex flex-col sm:flex-row gap-0 sm:gap-4'>
            <StoreButton storeType='app' />
            <StoreButton storeType='play' />
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    {
      q: 'How much can I actually earn?',
      a: 'Earnings depend on your email volume and pricing. Most users earn $20-200/month, with some earning $500+ by setting higher prices for specific outreach types.',
    },
    {
      q: 'How do I get paid?',
      a: "Senders pay through our secure payment system. You'll receive instant payments to your connected account with clear transaction records.",
    },
    {
      q: 'Can I set different prices for different types of emails?',
      a: 'Yes! Set a default price and custom rates for categories like sales outreach, recruiting, PR pitches, or individual senders.',
    },
    {
      q: 'What if I approve someone by mistake?',
      a: 'No problem—you can always change your mind. Approved senders can be moved back to paid status anytime.',
    },
  ];

  return (
    <section id='faq' className='py-14 bg-white'>
      <div className='mx-auto max-w-6xl px-4'>
        <h2 className='text-2xl md:text-3xl font-semibold tracking-tight'>
          Frequently asked questions
        </h2>
        <div className='mt-6 grid md:grid-cols-2 gap-6'>
          {faqs.map((f, i) => (
            <div key={i} className='rounded-2xl border p-5'>
              <div className='font-medium'>{f.q}</div>
              <div className='text-slate-600 mt-2 text-sm'>{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className='py-10'>
      <div className='mx-auto max-w-6xl px-4 grid md:grid-cols-2 gap-6 items-center'>
        <div className='text-sm text-slate-500'>
          © {new Date().getFullYear()} Trimbox. All rights reserved.
        </div>
        <div className='flex gap-4 justify-start md:justify-end text-sm'>
          <a href='https://www.trimbox.io/privacy' className='hover:text-blue-600'>
            Privacy
          </a>
          <a href='https://www.trimbox.io/terms' className='hover:text-blue-600'>
            Terms
          </a>
          <button
            type='button'
            className='hover:text-blue-600'
            onClick={() => {
              void ZendeskService.openSupport().catch(() => {
                window.open('https://trimbox.io/support');
              });
            }}
          >
            Contact
          </button>
        </div>
      </div>
    </footer>
  );
}

const StoreButton = ({
  storeType,
  size = 'default',
}: {
  storeType: 'app' | 'play';
  size?: 'small' | 'default';
}) => {
  const isAppStore = storeType === 'app';

  // Use consistent height but adjust width based on image proportions
  // App Store: 180x54, Play Store: 230x69
  const BUTTON_HEIGHT = size === 'small' ? 50 : 75;
  const BUTTON_WIDTH =
    size === 'small'
      ? isAppStore
        ? 110
        : 120 // Small: More balanced
      : isAppStore
      ? 165
      : 185; // Default: More balanced

  const link = isAppStore ? APP_STORE_LINK : PLAY_STORE_LINK;
  const imageSrc = isAppStore
    ? 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us'
    : 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';
  const altText = isAppStore ? 'Download on the App Store' : 'Get it on Google Play';

  return (
    <button
      onClick={() => openAppStoreLink(link)}
      className='inline-flex items-center justify-center border-none bg-transparent cursor-pointer hover:opacity-80 transition-opacity'
      style={{
        width: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
      }}
    >
      <img
        src={imageSrc}
        alt={altText}
        style={{
          height: '100%',
          width: 'auto',
          objectFit: 'contain',
          transform: isAppStore ? 'none' : 'scale(1.1)',
        }}
      />
    </button>
  );
};
